import { ChevronLeft, ChevronRight } from 'lucide-react';

interface StaffColumnCalendarProps {
  staff: any[];
  selectedStaffIds: string[];
  bookings: any[];
  onBookingClick: (bookingId: string) => void;
  onDateClick: (date: Date, staffId: string) => void;
  currentDate: Date;
  onDateChange: (date: Date) => void;
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#d97706',
  CONFIRMED: '#0284c7',
  IN_PROGRESS: '#7c3aed',
  COMPLETED: '#059669',
  CANCELLED: '#64748b',
  NO_SHOW: '#e11d48',
};

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => i).filter(h => h >= 8 && h <= 20);

export default function StaffColumnCalendar({
  staff,
  selectedStaffIds,
  bookings,
  onBookingClick,
  onDateClick,
  currentDate,
  onDateChange,
}: StaffColumnCalendarProps) {
  const selectedStaff = staff.filter(s => selectedStaffIds.includes(s.id));

  const handlePrevDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    onDateChange(prev);
  };

  const handleNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    onDateChange(next);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const getStaffColor = (staffId: string) => {
    const staffMember = staff.find(s => s.id === staffId);
    if (staffMember?.color) return staffMember.color;
    const index = staff.findIndex(s => s.id === staffId);
    return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
  };

  const getBookingsForStaff = (staffId: string) => {
    return bookings.filter(b => {
      const bookingDate = new Date(b.scheduledAt);
      return (
        b.staffId === staffId &&
        bookingDate.toDateString() === currentDate.toDateString()
      );
    });
  };

  const calculateBookingPosition = (scheduledAt: string, endsAt: string) => {
    const start = new Date(scheduledAt);
    const end = new Date(endsAt);

    const startHour = start.getHours();
    const startMinute = start.getMinutes();
    const endHour = end.getHours();
    const endMinute = end.getMinutes();

    const topOffset = ((startHour - 8) * 60 + startMinute) / 60;
    const duration = ((endHour - startHour) * 60 + (endMinute - startMinute)) / 60;

    return {
      top: `${topOffset * 4}rem`,
      height: `${duration * 4}rem`,
    };
  };

  const handleSlotClick = (hour: number, minute: number, staffId: string) => {
    const slotDate = new Date(currentDate);
    slotDate.setHours(hour, minute, 0, 0);
    onDateClick(slotDate, staffId);
  };

  if (selectedStaff.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500">
        Select staff members to view columns
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <button
          onClick={handlePrevDay}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous day"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold">
          {formatDate(currentDate)}
        </h2>

        <button
          onClick={handleNextDay}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next day"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="flex min-w-max">
          <div className="w-16 flex-shrink-0 border-r border-gray-200">
            <div className="h-16 border-b border-gray-200" />
            {TIME_SLOTS.map((hour) => (
              <div
                key={hour}
                className="h-16 border-b border-gray-200 flex items-start justify-center pt-1 text-xs text-gray-500"
              >
                {formatTime(hour)}
              </div>
            ))}
          </div>

          {selectedStaff.map((staffMember) => {
            const staffBookings = getBookingsForStaff(staffMember.id);
            const staffColor = getStaffColor(staffMember.id);

            return (
              <div
                key={staffMember.id}
                className="flex-1 min-w-[200px] border-r border-gray-200"
              >
                <div
                  className="h-16 border-b border-gray-200 flex items-center justify-center gap-2 font-medium"
                  style={{ borderTopColor: staffColor, borderTopWidth: 3 }}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: staffColor }}
                  />
                  <span>{staffMember.name}</span>
                </div>

                <div className="relative">
                  {TIME_SLOTS.map((hour) => (
                    <div key={hour}>
                      <div
                        className="h-8 border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => handleSlotClick(hour, 0, staffMember.id)}
                      />
                      <div
                        className="h-8 border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => handleSlotClick(hour, 30, staffMember.id)}
                      />
                    </div>
                  ))}

                  {staffBookings.map((booking) => {
                    const position = calculateBookingPosition(
                      booking.scheduledAt,
                      booking.endsAt
                    );
                    const statusColor = STATUS_COLORS[booking.status] || '#6b7280';

                    return (
                      <div
                        key={booking.id}
                        className="absolute left-1 right-1 bg-white border-l-4 rounded shadow-sm hover:shadow-md cursor-pointer transition-shadow overflow-hidden"
                        style={{
                          top: position.top,
                          height: position.height,
                          borderLeftColor: statusColor,
                          minHeight: '2rem',
                        }}
                        onClick={() => onBookingClick(booking.id)}
                      >
                        <div className="p-2 h-full flex flex-col text-xs">
                          <div className="flex items-center gap-1 mb-1">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: statusColor }}
                            />
                            <span className="font-semibold truncate">
                              {booking.customerName}
                            </span>
                          </div>
                          <div className="text-gray-600 truncate mb-1">
                            {booking.serviceName}
                          </div>
                          <div className="text-gray-500 text-[10px] mt-auto">
                            {new Date(booking.scheduledAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}{' '}
                            -{' '}
                            {new Date(booking.endsAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
